import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Clock, MessageCircle } from 'lucide-react';

const Contact = () => {
  const phoneNumber = '+56988985009';
  const instagramHandle = 'avicola_santa_adara';
  const tiktokHandle = 'avicola.santa.adara';

  return (
    <section
      id="contact"
      className="section-container bg-amber-600 relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-700/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="max-w-5xl mx-auto relative z-10 text-center">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="mb-12 md:mb-16"
        >
          <p className="text-amber-100 font-accent text-xl md:text-2xl mb-4">
            Contáctanos
          </p>
          <h2
            className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="contact-title"
          >
            ¡Haz Tu Pedido!
          </h2>
          <p className="text-xl md:text-2xl text-amber-100 max-w-2xl mx-auto leading-relaxed">
            Escríbenos por WhatsApp o síguenos en Instagram para conocer nuestras novedades
          </p>
        </motion.div>

        {/* Contact Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
        >
          {/* WhatsApp Button */}
          <a
            href={`https://wa.me/${phoneNumber.replace('+', '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="magnetic-btn bg-white text-stone-800 px-10 py-5 rounded-full font-bold text-xl flex items-center gap-4 hover:bg-stone-100 hover:scale-105 transition-all shadow-xl"
            data-testid="contact-whatsapp-btn"
          >
            <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </a>

          {/* Instagram Button */}
          <a
            href={`https://instagram.com/${instagramHandle}`}
            target="_blank"
            rel="noopener noreferrer"
            className="magnetic-btn bg-white text-stone-800 px-10 py-5 rounded-full font-bold text-xl flex items-center gap-4 hover:bg-stone-100 hover:scale-105 transition-all shadow-xl"
            data-testid="contact-instagram-btn"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
              <defs>
                <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#f09433" />
                  <stop offset="25%" stopColor="#e6683c" />
                  <stop offset="50%" stopColor="#dc2743" />
                  <stop offset="75%" stopColor="#cc2366" />
                  <stop offset="100%" stopColor="#bc1888" />
                </linearGradient>
              </defs>
              <path 
                d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
                fill="url(#instagram-gradient)"
              />
            </svg>
            @{instagramHandle}
          </a>

          {/* TikTok Button */}
          <a
            href={`https://www.tiktok.com/@${tiktokHandle}`}
            target="_blank"
            rel="noopener noreferrer"
            className="magnetic-btn bg-white text-stone-800 px-10 py-5 rounded-full font-bold text-xl flex items-center gap-4 hover:bg-stone-100 hover:scale-105 transition-all shadow-xl"
            data-testid="contact-tiktok-btn"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" fill="#000"/>
            </svg>
            @{tiktokHandle}
          </a>
        </motion.div>

        {/* Phone Number Display */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12"
        >
          <a 
            href={`tel:${phoneNumber}`}
            className="inline-flex items-center gap-3 text-white hover:text-amber-100 transition-colors"
            data-testid="contact-phone"
          >
            <Phone className="w-6 h-6" />
            <span className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              +56 9 8898 5009
            </span>
          </a>
        </motion.div>

        {/* Info Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto"
        >
          <div className="bg-amber-700/50 backdrop-blur-sm rounded-2xl p-6 text-left">
            <div className="flex items-center gap-3 mb-3">
              <MessageCircle className="w-6 h-6 text-amber-100" />
              <h4 className="text-lg font-semibold text-white">Pedidos</h4>
            </div>
            <p className="text-amber-100">
              Escríbenos por WhatsApp para realizar tu pedido. Te responderemos lo antes posible.
            </p>
          </div>

          <div className="bg-amber-700/50 backdrop-blur-sm rounded-2xl p-6 text-left">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="w-6 h-6 text-amber-100" />
              <h4 className="text-lg font-semibold text-white">Horario</h4>
            </div>
            <p className="text-amber-100">
              Atendemos de Lunes a Sábado. Consulta disponibilidad y precios por WhatsApp.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
